// PARITY-0: the board settings sheet — a right sheet with a tab strip over the registered tabs
// (registry.ts; General is always first) and the active tab's panel. It owns no settings itself:
// each tab is its feature's component, handed the board and what the viewer may do. wp/ACTIONS:
// mounted from the board header (a Board settings icon and "Board settings…" in its "…" menu),
// with `can` from the board's access and the status-set write.
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@companyos/ui";
import { useState } from "react";
import "./general-tab";
import "./fields-tab";
import "./wakes-tab";
import "./views-tab";
// B4-ITEM-TEMPLATES
import "./templates-tab";
import "./how-it-works-tab";
import "./gates-tab";
import { type BoardSettingsTabProps, visibleBoardSettingsTabs } from "./registry";

export function BoardSettingsSheet({
  board,
  can,
  saveStatuses,
  api,
  co,
  company,
  open,
  onOpenChange,
  tab,
}: BoardSettingsTabProps & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The tab to open on (default: the first, General). */
  tab?: string | undefined;
}) {
  const tabs = visibleBoardSettingsTabs({ board, can, api, co });
  const [current, setCurrent] = useState<string | undefined>(tab);
  const active = tabs.some((t) => t.id === current) ? current : tabs[0]?.id;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 p-0 data-[side=right]:sm:max-w-[34rem]"
        data-slot="board-settings-sheet"
      >
        <SheetHeader className="h-14 justify-center border-b border-subtle py-0 pl-4">
          <SheetTitle>Board settings</SheetTitle>
          <SheetDescription className="sr-only">Settings of {board.name}.</SheetDescription>
        </SheetHeader>
        {active ? (
          <Tabs
            value={active}
            onValueChange={(v) => setCurrent(String(v))}
            className="min-h-0 flex-1"
          >
            {/* Seven tabs fit at 1440 px (the sheet is wide enough); a phone swipes the row, with no
                scroll thumb. */}
            <TabsList variant="line" className="no-scrollbar w-full px-4">
              {tabs.map((t) => (
                <TabsTrigger key={t.id} value={t.id}>
                  {t.title}
                </TabsTrigger>
              ))}
            </TabsList>
            {tabs.map((t) => (
              <TabsContent key={t.id} value={t.id} className="overflow-y-auto p-4">
                <t.Component
                  board={board}
                  can={can}
                  saveStatuses={saveStatuses}
                  api={api}
                  co={co}
                  company={company}
                />
              </TabsContent>
            ))}
          </Tabs>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
